import React, {useMemo, useState} from 'react';
import {WordList} from '../../domain/types';
import {MAX_WORDLISTS_PER_CONVERSION} from '../../store/slices/conversionsThunks';
import {AlertTriangleIcon, CheckIcon, SearchIcon, StarIcon, XIcon} from '../Icons';

interface WordListMultiSelectProps {
  /** Everything the user can convert against — `myWordlistsSelector`. */
  lists: WordList[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /**
   * Ids the server refused with `wordlist_not_found`. Marked in place rather than dropped, because
   * the point is for the user to see which of their selections is the problem and deselect it —
   * and a deleted list is usually not in `lists` at all, so the chip row is the only place it can
   * be shown or removed.
   */
  missingIds?: string[];
  /** Red border on the box, for a submit blocked by "choose at least one". */
  invalid?: boolean;
  disabled?: boolean;
  /** Where to send someone who has no lists yet. Rendered as plain text, so keep it short. */
  emptyHint?: string;
  max?: number;
}

/**
 * Pick the word lists a conversion is run against — up to five, merged server-side.
 *
 * A `<select>` was fine while a conversion took exactly one list. Choosing several is a different
 * task: you have to see what you have picked while you keep looking, and picking well now matters
 * enough that each list's own description is worth the row height (several official lists are
 * explicitly meant to be *stacked* on a profanity list rather than used alone, and their
 * descriptions are the only thing that says so).
 *
 * Three things this has to get right, none of them obvious:
 *
 * - **The chips are not decoration.** A list that has been deleted since it was picked is not in
 *   `lists`, so a checkbox list alone could show a selection the user cannot see or clear. Every
 *   selected id gets a chip whether or not it resolves to a list.
 * - **The cap is enforced here, not just reported.** Unselected rows go inert at the limit with the
 *   reason attached, so the 400 (`too_many_wordlists`) is a backstop rather than the way the user
 *   finds out.
 * - **Overlap is not a problem to solve.** The server merges and de-duplicates, so a word on three
 *   of the lists still cuts once. There is deliberately no warning about it, and no reordering:
 *   order only decides which list gets credited in the response stats.
 */
const WordListMultiSelect: React.FC<WordListMultiSelectProps> = ({
  lists,
  selectedIds,
  onChange,
  missingIds = [],
  invalid = false,
  disabled = false,
  emptyHint,
  max = MAX_WORDLISTS_PER_CONVERSION,
}) => {
  const [search, setSearch] = useState('');

  const byId = useMemo(
    () => new Map(lists.map(list => [list.id, list])),
    [lists]
  );

  const {owned, added} = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = query
      ? lists.filter(list =>
        list.name.toLowerCase().includes(query)
        || (list.description ?? '').toLowerCase().includes(query))
      : lists;

    return {
      owned: matching.filter(list => list.isOwner !== false),
      added: matching.filter(list => list.isOwner === false),
    };
  }, [lists, search]);

  const isAtLimit = selectedIds.length >= max;

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(selected => selected !== id));
      return;
    }
    if (isAtLimit) {
      return;
    }
    // Appended rather than inserted: the order is the order they were picked, which is the order
    // they are applied and credited in.
    onChange([...selectedIds, id]);
  };

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-gray-300">Word lists</span>
        <span className="flex items-center gap-2 text-[11px] text-gray-500">
          <span className="tabular-nums">{selectedIds.length} of {max} selected</span>
          {selectedIds.length > 0 && !disabled && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-gray-400 underline hover:text-gray-200 transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </span>
      </div>

      {selectedIds.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedIds.map((id) => {
            const list = byId.get(id);
            const isMissing = missingIds.includes(id);

            return (
              <span
                key={id}
                title={isMissing
                  ? 'This list is no longer available. Remove it to convert.'
                  : list?.description || list?.name}
                className={`inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1
                 text-xs ${
              isMissing
                ? 'border-red-500/40 bg-red-500/[0.08] text-red-200'
                : 'border-indigo-500/30 bg-indigo-500/10 text-indigo-200'
              }`}
              >
                {isMissing && <AlertTriangleIcon className="w-3 h-3 shrink-0" />}
                <span className="truncate">{list?.name ?? 'Unavailable list'}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onChange(selectedIds.filter(selected => selected !== id))}
                    aria-label={`Remove ${list?.name ?? 'this list'}`}
                    className="shrink-0 rounded hover:text-white transition-colors cursor-pointer"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      <div className="relative mb-2">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
        <input
          type="search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          disabled={disabled}
          placeholder="Search word lists…"
          aria-label="Search word lists"
          className="w-full rounded-lg border border-gray-800 bg-gray-950/60 py-2 pl-8 pr-3 text-sm
           text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
           focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
        />
      </div>

      <div
        aria-invalid={invalid}
        className={`max-h-56 overflow-y-auto rounded-lg border bg-gray-950/40 ${
          invalid ? 'border-red-500/50' : 'border-gray-800'
        }`}
      >
        {owned.length === 0 && added.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-gray-500">
            {lists.length === 0
              ? emptyHint ?? 'You have no word lists yet. Create one or add one from the marketplace.'
              : 'No word lists match that search.'}
          </p>
        )}
        <WordListGroup
          heading="My lists"
          lists={owned}
          selectedIds={selectedIds}
          missingIds={missingIds}
          isAtLimit={isAtLimit}
          max={max}
          disabled={disabled}
          onToggle={toggle}
        />
        <WordListGroup
          heading="Added from the marketplace"
          lists={added}
          selectedIds={selectedIds}
          missingIds={missingIds}
          isAtLimit={isAtLimit}
          max={max}
          disabled={disabled}
          onToggle={toggle}
        />
      </div>

      {isAtLimit && (
        <p className="mt-1 text-[11px] text-gray-500">
          That&rsquo;s the maximum of {max}. Remove one to pick a different list.
        </p>
      )}
    </div>
  );
};

const WordListGroup: React.FC<{
  heading: string;
  lists: WordList[];
  selectedIds: string[];
  missingIds: string[];
  isAtLimit: boolean;
  max: number;
  disabled: boolean;
  onToggle: (id: string) => void;
}> = ({heading, lists, selectedIds, missingIds, isAtLimit, max, disabled, onToggle}) => {
  if (lists.length === 0) {
    return null;
  }

  return (
    <>
      <p className="sticky top-0 z-10 bg-gray-950/90 px-3 py-1.5 text-[11px] font-medium uppercase
       tracking-wide text-gray-600 backdrop-blur">
        {heading}
      </p>
      {lists.map((list) => {
        const isSelected = selectedIds.includes(list.id);
        const isMissing = isSelected && missingIds.includes(list.id);
        // Only unselected rows go inert at the cap — deselecting is how you get back under it.
        const isBlockedByLimit = !isSelected && isAtLimit;
        const isDisabled = disabled || isBlockedByLimit;

        return (
          <button
            key={list.id}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            disabled={isDisabled}
            title={isBlockedByLimit ? `You can combine up to ${max} word lists.` : undefined}
            onClick={() => onToggle(list.id)}
            className={`flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors ${
              isDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
            } ${
              isMissing
                ? 'bg-red-500/[0.06]'
                : isSelected
                  ? 'bg-indigo-500/10'
                  : 'hover:bg-gray-800/60'
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                isMissing
                  ? 'border-red-500/60 bg-red-500/20 text-red-200'
                  : isSelected
                    ? 'border-indigo-500 bg-indigo-600 text-white'
                    : 'border-gray-700'
              }`}
            >
              {isSelected && <CheckIcon className="w-3 h-3" />}
            </span>

            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                {list.isOfficial && (
                  <StarIcon className="w-3 h-3 shrink-0 text-amber-400" />
                )}
                <span className={`truncate text-sm ${
                  isMissing ? 'text-red-200' : isSelected ? 'text-indigo-200' : 'text-gray-300'
                }`}
                >
                  {list.name}
                </span>
                <span className="ml-auto shrink-0 text-xs tabular-nums text-gray-600">
                  {list.wordCount ?? list.words?.length ?? 0} words
                </span>
              </span>
              {/* The descriptions carry real guidance now that lists get stacked — several official
                  ones are only correct on top of a profanity list, and say so here. */}
              {list.description && (
                <span className="mt-0.5 block text-[11px] leading-snug text-gray-500 line-clamp-2">
                  {list.description}
                </span>
              )}
              {isMissing && (
                <span className="mt-0.5 block text-[11px] text-red-300">
                  No longer available — remove it to convert.
                </span>
              )}
            </span>
          </button>
        );
      })}
    </>
  );
};

export default WordListMultiSelect;
